import React from "react";
import { Button, Flex, LoadingIndicator, ModalV2, Text } from "@jsq/ds/core";
import { useGetNocPaymentDetailsQuery } from "#graphql/supergraphHooks";
import { formatCalendarDate } from "#utils/datetime_utils";
import { clientContext } from "#utils/graphql_utils";
import Money from "../../../Money";
import * as styles from "./styles_vanilla.css";

export type NocPaymentDetailsModalProps = {
  paymentId: string | null;
  onClose: () => void;
};

const formatStatus = (status: string): string =>
  status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

const REFERENCE_LABELS: Record<string, string> = {
  ACH: "ACH trace number",
  WIRE: "Fedwire IMAD",
};

export const NocPaymentDetailsModal: React.FC<NocPaymentDetailsModalProps> = ({
  paymentId,
  onClose,
}) => {
  const normalizedPaymentId = paymentId?.trim() || null;
  const { data, loading, error } = useGetNocPaymentDetailsQuery({
    variables: { id: normalizedPaymentId ?? "" },
    context: clientContext(),
    fetchPolicy: "cache-first",
    skip: normalizedPaymentId == null,
  });

  const payment = data?.payment;

  return (
    <ModalV2
      show={normalizedPaymentId != null}
      onHide={onClose}
      title="Payment details"
      data-testid="noc-payment-details-modal"
    >
      <ModalV2.Body>
        {loading && (
          <Flex justifyContent="center" alignItems="center">
            <LoadingIndicator />
          </Flex>
        )}
        {error != null && (
          <Text variant="regular-md" color="muted">
            Unable to load payment details.
          </Text>
        )}
        {payment == null && !loading && error == null && (
          <Text variant="regular-md" color="muted">
            Payment details not found.
          </Text>
        )}
        {payment != null && !loading && (
          <dl>
            <Flex flexDirection="column" gap={16}>
              {[
                { label: "Account", value: payment.position?.account?.name },
                {
                  label: "Payment amount",
                  value: <Money money={payment.amount} rounded={false} />,
                },
                { label: "Payment method", value: payment.method },
                ...(payment.bankAccount?.bankInfoRedacted != null
                  ? [{ label: "Bank account", value: payment.bankAccount.bankInfoRedacted }]
                  : []),
                { label: "Payment ID", value: payment.id },
                ...(payment.referenceNumber != null
                  ? [
                      {
                        label: REFERENCE_LABELS[payment.method] ?? "Reference number",
                        value: payment.referenceNumber,
                      },
                    ]
                  : []),
                ...(payment.scheduledProcessDate != null
                  ? [
                      {
                        label: "Payment date",
                        value: formatCalendarDate(payment.scheduledProcessDate),
                      },
                    ]
                  : []),
                { label: "Payment status", value: formatStatus(payment.status) },
              ].map(({ label, value }) => (
                <Flex key={label} gap={32}>
                  <dt className={styles.modalLabel}>
                    <Text variant="secondary-md">{label}</Text>
                  </dt>
                  <dd>
                    <Text variant="regular-md" asSpan={typeof value !== "string"}>
                      {value}
                    </Text>
                  </dd>
                </Flex>
              ))}
            </Flex>
          </dl>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end">
          <Button variant="neutral" onClick={onClose}>
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
